import React, { useState, useEffect } from 'react'
import {
  Truck,
  AlertTriangle,
  CheckCircle,
  Clock,
  Plus,
  Search,
  Settings,
  Activity,
  Edit,
  Eye,
  ChevronUp,
  ChevronDown,
  Link2,
  Unlink,
  X,
  Battery,
  Boxes,
  ArrowRightLeft,
  Map as MapIcon
} from 'lucide-react'
import VehicleCreationWizard from './VehicleCreationWizard'
import VehicleDetailsModal, { VehicleModalTab } from './VehicleDetailsModal'
import FleetGroupsPanel from './FleetGroupsPanel'
import { apiClient } from '../config/api'
import { fleetColor } from '../utils/fleetColors'
import { trackingPresentation, vehicleIconComponent } from '../utils/vehiclePresentation'
import { baliseService } from '../services/baliseService'
import type { Balise } from '../types/balise'
import type { ControlCenterMission, ControlCenterRule, ControlCenterZone, ZoneType } from '../services/vehicleControlCenterTypes'
import { useTranslation } from 'react-i18next'

// =====================================================
// INTERFACES
// =====================================================

export interface FleetVehicle {
  id: number
  vehicle_code: string
  license_plate: string
  make: string
  brand?: string
  model: string
  year: number
  vehicle_type: string
  asset_type?: string
  icon_key?: string
  operational_identifier: string
  equipment_reference?: string
  container_type?: string
  container_owner?: string
  container_origin?: string
  container_destination?: string
  booking_reference?: string
  vehicle_source: 'ASSET' | 'CONTAINER'
  vehicle_category: string
  operational_status: 'ACTIVE' | 'MAINTENANCE' | 'OUT_OF_SERVICE' | 'RETIRED'
  fuel_type: string
  max_payload: number
  alert_status: 'OK' | 'NO_SIGNAL' | 'LOW_BATTERY' | 'NOT_TRACKED'
  home_depot: string
  created_at: Date
  balise_codes?: string // Lock codes and tracking info
  balise_count: number
  balise_statuses?: string
  // Honest tracking data (from balise telemetry, computed server-side)
  tracking_status: 'LIVE' | 'STALE' | 'OFFLINE' | 'NOT_TRACKED'
  current_speed?: number
  balise_last_seen?: string
  balise_battery?: number
  last_latitude?: number
  last_longitude?: number
  customer_name?: string
  customer_code?: string
  customer_id?: number
  custom_name?: string
  current_location?: string
  /** Names of the fleets this vehicle belongs to (decorated from memberships). */
  fleets: string[]
  // Real vehicle specifications from database
  length?: number
  width?: number
  height?: number
  weight?: number
  max_weight?: number
  engine_type?: string
  consumption?: number
  max_speed?: number
}

interface VehicleStats {
  total_vehicles: number
  active_vehicles: number
  // Tracking KPIs
  tracked_vehicles: number
  live_vehicles: number
  not_tracked_vehicles: number
  tracking_alerts: number
}

interface FleetVehicleOperationsProps {
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  /** Opens the Fleet Map window focused on the given vehicle. */
  onShowOnMap?: (vehicle: { vehicle_source: 'ASSET' | 'CONTAINER', id: number, vehicle_code: string, balise_codes?: string }) => void
  onCreateVehicleZone?: (type: ZoneType, vehicle: FleetVehicle) => boolean | void
  onManageVehicleZone?: (zone: ControlCenterZone, type: ZoneType, vehicle: FleetVehicle) => void
  onManageVehicleMission?: (mission: ControlCenterMission, vehicle: FleetVehicle) => void
  onCreateVehicleAlertRule?: (vehicle: FleetVehicle) => void
  onEditVehicleAlertRule?: (rule: ControlCenterRule, vehicle: FleetVehicle) => void
  onOpenVehicleControlCenter?: (vehicle: FleetVehicle, tab: VehicleModalTab, startEditing: boolean) => void
}

// Tracking alert priority: no signal > low battery > untracked > OK
function computeTrackingAlert(vehicle: any): FleetVehicle['alert_status'] {
  const tracking = vehicle.tracking_status || 'NOT_TRACKED'
  if (tracking === 'STALE' || tracking === 'OFFLINE') return 'NO_SIGNAL'
  const battery = vehicle.balise_battery != null ? Number(vehicle.balise_battery) : null
  if (tracking === 'LIVE' && battery != null && battery < 20) return 'LOW_BATTERY'
  if (tracking === 'NOT_TRACKED') return 'NOT_TRACKED'
  return 'OK'
}

function formatLastSeen(iso?: string): string {
  if (!iso) return ''
  const diffMs = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} h ago`
  return `${Math.floor(hours / 24)} d ago`
}

// =====================================================
// MAIN COMPONENT
// =====================================================

const FleetVehicleOperations: React.FC<FleetVehicleOperationsProps> = ({
  showMessage,
  onShowOnMap,
  onCreateVehicleZone,
  onManageVehicleZone,
  onManageVehicleMission,
  onCreateVehicleAlertRule,
  onEditVehicleAlertRule,
  onOpenVehicleControlCenter
}) => {
  const { t } = useTranslation()
  // State Management
  const [activeTab, setActiveTab] = useState<'overview' | 'fleets'>('overview')
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([])
  const [vehicleStats, setVehicleStats] = useState<VehicleStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [customerFilter, setCustomerFilter] = useState<string>('all')
  const [fleetFilter, setFleetFilter] = useState<string>('all')
  // KPI drill-down: clicking a stats card filters the table to those vehicles
  const [kpiFilter, setKpiFilter] = useState<'all' | 'LIVE' | 'TRACKED' | 'NOT_TRACKED' | 'ALERTS'>('all')

  const [allFleets, setAllFleets] = useState<{ id: number, name: string, member_count: number }[]>([])
  // Per-vehicle fleet association dialog (replaces the confusing checkbox
  // multi-select: one explicit associate/dissociate action per row)
  const [fleetDialogVehicle, setFleetDialogVehicle] = useState<FleetVehicle | null>(null)
  const [fleetDialogFleetId, setFleetDialogFleetId] = useState('')
  const [fleetDialogNewName, setFleetDialogNewName] = useState('')
  const [fleetDialogBusy, setFleetDialogBusy] = useState(false)

  // Assignment workflow state
  const [unassignTarget, setUnassignTarget] = useState<FleetVehicle | null>(null)
  const [unassignBusy, setUnassignBusy] = useState(false)
  const [assignTarget, setAssignTarget] = useState<FleetVehicle | null>(null)
  const [availableBalises, setAvailableBalises] = useState<Balise[]>([])
  const [assignLoading, setAssignLoading] = useState(false)
  const [assignBusy, setAssignBusy] = useState(false)
  const [selectedBalise, setSelectedBalise] = useState<Balise | null>(null)
  const [assignSearch, setAssignSearch] = useState('')
  const [assignBrandFilter, setAssignBrandFilter] = useState<string>('all')
  
  // Sorting state
  const [sortField, setSortField] = useState<keyof FleetVehicle>('vehicle_code')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  
  // Modal and Form State
  const [showNewVehicleModal, setShowNewVehicleModal] = useState(false)
  const [showVehicleDetailsModal, setShowVehicleDetailsModal] = useState(false)
  const [selectedVehicleForDetails, setSelectedVehicleForDetails] = useState<FleetVehicle | null>(null)
  const [vehicleDetailsTab, setVehicleDetailsTab] = useState<VehicleModalTab>('history')
  const [vehicleModalEditing, setVehicleModalEditing] = useState(false)
  
  // REAL INTEGRATION: Load vehicles from orchestration database with all real data
  // NOTE: must NOT touch `loading` — it's also called by the 30 s auto-refresh
  // and by modal callbacks, where flipping `loading` would replace the whole
  // window with the spinner (and never clear it).
  const loadVehicleData = async () => {
    try {
      console.log('🔄 Loading ALL vehicles from orchestration database...')
      
      // Load vehicles data which includes ALL assets and containers with balise info,
      // plus fleet memberships to decorate each row with its fleets.
      const [vehiclesResponse, membershipsResponse, fleetsResponse] = await Promise.all([
        apiClient.get('/vehicles'),
        apiClient.get('/fleets/memberships').catch(() => ({ data: [] })),
        apiClient.get('/fleets').catch(() => ({ data: [] }))
      ])
      const vehicleData = vehiclesResponse.data || []
      setAllFleets(fleetsResponse.data || [])
      const fleetsByVehicle = new Map<string, string[]>()
      for (const m of (membershipsResponse.data || [])) {
        const key = `${m.vehicle_source}-${m.vehicle_id}`
        if (!fleetsByVehicle.has(key)) fleetsByVehicle.set(key, [])
        fleetsByVehicle.get(key)!.push(m.fleet_name)
      }
      
      console.log(`📊 Loaded ${vehicleData.length} vehicles (assets + containers) from orchestration DB`)
      
      if (vehicleData.length > 0) {
        console.log('📋 Sample vehicle data from DB:', vehicleData[0])
      }
      
      // Transform real database data to FleetVehicle format — only fields we
      // actually have. All fabricated values (random mileage, invented
      // insurance/registration dates) were removed 2026-07-09.
      const allVehicles: FleetVehicle[] = vehicleData.map((vehicle: any) => {
        const manufacturingYear = vehicle.year_manufactured ||
          (vehicle.created_at ? new Date(vehicle.created_at).getFullYear() : new Date().getFullYear())

        const vehicleType = vehicle.vehicle_source === 'ASSET'
          ? (vehicle.asset_type || vehicle.vehicle_type || 'TRUCK')
          : 'CONTAINER'
        const licensePlate = vehicle.vehicle_source === 'ASSET' ? String(vehicle.license_plate || '') : ''
        const equipmentReference = vehicle.equipment_reference || vehicle.vehicle_code
        
        return {
          id: vehicle.vehicle_id,
          vehicle_code: vehicle.vehicle_code, // Always use the actual vehicle/container code
          license_plate: licensePlate,
          make: vehicle.brand || vehicle.make || '',
          brand: vehicle.brand || vehicle.make || '',
          model: vehicle.vehicle_source === 'ASSET' ? (vehicle.model || '') : '',
          year: manufacturingYear,
          vehicle_type: vehicleType,
          asset_type: vehicle.asset_type,
          icon_key: vehicle.icon_key,
          operational_identifier: vehicle.vehicle_source === 'ASSET'
            ? (licensePlate || vehicle.vehicle_code)
            : equipmentReference,
          equipment_reference: equipmentReference,
          container_type: vehicle.container_type,
          container_owner: vehicle.container_owner,
          container_origin: vehicle.container_origin,
          container_destination: vehicle.container_destination,
          booking_reference: vehicle.booking_reference,
          vehicle_source: vehicle.vehicle_source,
          vehicle_category: vehicle.vehicle_source === 'ASSET' ? 'HEAVY_DUTY' : 'CONTAINER',
          operational_status: vehicle.vehicle_status === 'ACTIVE' ? 'ACTIVE' : 'OUT_OF_SERVICE',
          
          // REAL SPECIFICATIONS FROM DATABASE
          fuel_type: vehicle.engine_type || 'DIESEL',
          max_payload: vehicle.max_weight || 0,
          
          // REAL CUSTOMER AND DRIVER INFO
          customer_name: vehicle.customer_name,
          customer_code: vehicle.customer_code,
          customer_id: vehicle.customer_id,
          custom_name: vehicle.custom_name && !vehicle.custom_name.includes('Maxvision') ? vehicle.custom_name : null,
          current_location: vehicle.current_location,
          fleets: fleetsByVehicle.get(`${vehicle.vehicle_source}-${vehicle.vehicle_id}`) || [],
          
          // REAL BALISE INFO FROM DATABASE
          balise_codes: vehicle.balise_codes,
          // PostgreSQL COUNT values arrive as strings. Normalize here so KPI
          // drill-down comparisons such as `balise_count === 0` remain valid.
          balise_count: Number(vehicle.balise_count) || 0,
          balise_statuses: vehicle.balise_statuses,

          // HONEST TRACKING STATUS (server-computed from balise telemetry)
          tracking_status: vehicle.tracking_status || 'NOT_TRACKED',
          current_speed: vehicle.current_speed == null ? undefined : Number(vehicle.current_speed),
          balise_last_seen: vehicle.balise_last_seen,
          balise_battery: vehicle.balise_battery != null ? Number(vehicle.balise_battery) : undefined,
          last_latitude: vehicle.last_latitude != null ? Number(vehicle.last_latitude) : undefined,
          last_longitude: vehicle.last_longitude != null ? Number(vehicle.last_longitude) : undefined,
          
          // REAL VEHICLE SPECIFICATIONS
          length: vehicle.length,
          width: vehicle.width,
          height: vehicle.height,
          weight: vehicle.weight,
          max_weight: vehicle.max_weight,
          engine_type: vehicle.engine_type,
          consumption: vehicle.consumption,
          max_speed: vehicle.max_speed,
          
          // Alerts derive from tracking reality, not admin status
          alert_status: computeTrackingAlert(vehicle),
          home_depot: vehicle.current_location || 'Main Depot',
          created_at: new Date(vehicle.created_at || Date.now())
        }
      })
      
      console.log(`✅ Transformed ${allVehicles.length} real vehicles from orchestration database`)
      return allVehicles
      
    } catch (error) {
      console.error('❌ Error loading vehicle data from orchestration DB:', error)
      return []
    }
  }

  // Calculate real statistics from database data
  const calculateStats = (vehicles: FleetVehicle[]): VehicleStats => {
    const activeVehicles = vehicles.filter(v => v.operational_status === 'ACTIVE')
    // Tracking KPIs — the numbers that matter for a tracking platform
    const tracked = vehicles.filter(v => v.balise_count > 0)
    const live = vehicles.filter(v => v.tracking_status === 'LIVE')
    const trackingAlerts = vehicles.filter(v => v.alert_status === 'NO_SIGNAL' || v.alert_status === 'LOW_BATTERY').length
    
    return {
      total_vehicles: vehicles.length,
      active_vehicles: activeVehicles.length,
      tracked_vehicles: tracked.length,
      live_vehicles: live.length,
      not_tracked_vehicles: vehicles.length - tracked.length,
      tracking_alerts: trackingAlerts
    }
  }

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const vehicleData = await loadVehicleData()
        setVehicles(vehicleData)
        setVehicleStats(calculateStats(vehicleData))
        
        const actualCount = vehicleData.length
        const assetCount = vehicleData.filter(v => v.vehicle_source === 'ASSET').length
        const containerCount = vehicleData.filter(v => v.vehicle_source === 'CONTAINER').length
        showMessage?.(t('vehicles_loaded_msg', { defaultValue: '{{count}} vehicles loaded ({{assets}} assets, {{containers}} containers)', count: actualCount, assets: assetCount, containers: containerCount }), 'success')
      } catch (error) {
        console.error('Error loading vehicles:', error)
        showMessage?.(t('vehicles_load_failed', { defaultValue: 'Unable to load vehicles. Please try again.' }), 'error')
      } finally {
        setLoading(false)
      }
    }
    
    loadData()
  }, [])

  // Auto-refresh tracking data every 30 s (aligned with backend sync cadence)
  // so Live / No-signal badges stay current. Skipped while an assignment
  // dialog is open to avoid the list re-sorting under the user.
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'overview' && !assignTarget && !unassignTarget && !showVehicleDetailsModal && !showNewVehicleModal) {
        refreshVehicles().catch(() => {})
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [activeTab, assignTarget, unassignTarget, showVehicleDetailsModal, showNewVehicleModal])

  // Vehicle Action Handlers
  const handleNewVehicle = () => {
    setShowNewVehicleModal(true)
  }

  // View = activity timeline (read-only). Edit = Details tab already in edit mode.
  const handleViewVehicle = (vehicle: FleetVehicle) => {
    if (onOpenVehicleControlCenter) {
      onOpenVehicleControlCenter(vehicle, 'history', false)
      return
    }
    setSelectedVehicleForDetails(vehicle)
    setVehicleDetailsTab('history')
    setVehicleModalEditing(false)
    setShowVehicleDetailsModal(true)
  }

  const handleEditVehicle = (vehicle: FleetVehicle) => {
    if (onOpenVehicleControlCenter) {
      onOpenVehicleControlCenter(vehicle, 'details', true)
      return
    }
    setSelectedVehicleForDetails(vehicle)
    setVehicleDetailsTab('details')
    setVehicleModalEditing(true)
    setShowVehicleDetailsModal(true)
  }

  const vehicleKey = (v: FleetVehicle) => `${v.vehicle_source}-${v.id}`

  // --- Per-vehicle fleet association (explicit associate / dissociate) ---
  const openFleetDialog = (vehicle: FleetVehicle) => {
    setFleetDialogVehicle(vehicle)
    setFleetDialogFleetId('')
    setFleetDialogNewName('')
  }

  const assignVehicleToFleet = async (fleetId: number, fleetName: string) => {
    if (!fleetDialogVehicle) return
    const v = fleetDialogVehicle
    setFleetDialogBusy(true)
    try {
      const res = await apiClient.post(`/fleets/${fleetId}/members`, { vehicle_source: v.vehicle_source, vehicle_id: v.id })
      const movedFrom = (res.data?.moved_from_fleets || []) as { id: number, name: string }[]
      if (movedFrom.length > 0) {
        showMessage?.(t('moved_to_fleet', {
          defaultValue: '{{vehicle}} moved from {{from}} to {{to}}',
          vehicle: v.vehicle_code, from: movedFrom.map(f => f.name).join(', '), to: fleetName
        }), 'success')
      } else {
        showMessage?.(t('added_to_fleet_named', { defaultValue: '{{vehicle}} added to fleet {{fleet}}', vehicle: v.vehicle_code, fleet: fleetName }), 'success')
      }
      setFleetDialogVehicle(null)
      await refreshVehicles()
    } catch (error: any) {
      const msg = error?.response?.data?.error || t('add_to_fleet_failed', { defaultValue: 'Failed to add vehicle to fleet' })
      showMessage?.(msg, 'error')
    } finally {
      setFleetDialogBusy(false)
    }
  }

  const createFleetAndAssign = async () => {
    const name = fleetDialogNewName.trim()
    if (!name) return
    setFleetDialogBusy(true)
    try {
      const res = await apiClient.post('/fleets', { name })
      await assignVehicleToFleet(res.data.id, res.data.name)
    } catch (error: any) {
      setFleetDialogBusy(false)
      const msg = error?.response?.data?.error || t('fleet_create_failed', { defaultValue: 'Failed to create fleet' })
      showMessage?.(msg, 'error')
    }
  }

  const dissociateVehicleFromFleet = async () => {
    if (!fleetDialogVehicle) return
    const v = fleetDialogVehicle
    const currentName = v.fleets[0]
    const fleet = allFleets.find(f => f.name === currentName)
    if (!fleet) return
    setFleetDialogBusy(true)
    try {
      await apiClient.delete(`/fleets/${fleet.id}/members/${v.vehicle_source}/${v.id}`)
      showMessage?.(t('removed_from_fleet', { defaultValue: '{{vehicle}} removed from {{fleet}}', vehicle: v.vehicle_code, fleet: fleet.name }), 'success')
      setFleetDialogVehicle(null)
      await refreshVehicles()
    } catch (error) {
      console.error('Remove from fleet error:', error)
      showMessage?.(t('remove_from_fleet_failed', { defaultValue: 'Failed to remove vehicle from fleet' }), 'error')
    } finally {
      setFleetDialogBusy(false)
    }
  }

  // In-flight guard: if the API is slow, the 30 s interval must not stack
  // parallel requests on top of each other.
  const refreshInFlight = React.useRef(false)
  const refreshVehicles = async () => {
    if (refreshInFlight.current) return
    refreshInFlight.current = true
    try {
      const vehicleData = await loadVehicleData()
      setVehicles(vehicleData)
      setVehicleStats(calculateStats(vehicleData))
    } finally {
      refreshInFlight.current = false
    }
  }

  // --- Unassign balise from vehicle (with confirmation naming the balise) ---
  const confirmUnassign = async () => {
    if (!unassignTarget) return
    const lockCode = (unassignTarget.balise_codes || '').split(',')[0]?.trim()
    if (!lockCode) {
      showMessage?.(t('no_balise_to_unassign', { defaultValue: 'No balise to unassign' }), 'warning')
      setUnassignTarget(null)
      return
    }
    setUnassignBusy(true)
    try {
      await baliseService.unassignBalise(lockCode)
      showMessage?.(t('balise_unassigned_from', { defaultValue: 'Balise {{code}} unassigned from {{vehicle}}', code: lockCode, vehicle: unassignTarget.vehicle_code }), 'success')
      setUnassignTarget(null)
      await refreshVehicles()
    } catch (error) {
      console.error('Unassign error:', error)
      showMessage?.(t('unassign_failed', { defaultValue: 'Failed to unassign balise' }), 'error')
    } finally {
      setUnassignBusy(false)
    }
  }

  // --- Assign a balise to an untracked vehicle (picker of available balises) ---
  const openAssignPicker = async (vehicle: FleetVehicle) => {
    setAssignTarget(vehicle)
    setSelectedBalise(null)
    setAssignSearch('')
    setAssignBrandFilter('all')
    setAssignLoading(true)
    try {
      const result = await baliseService.getAllBalises(1, 200)
      setAvailableBalises(result.data.filter(b => !b.isAssigned))
    } catch (error) {
      console.error('Error loading available balises:', error)
      setAvailableBalises([])
      showMessage?.(t('balises_load_failed', { defaultValue: 'Failed to load available balises' }), 'error')
    } finally {
      setAssignLoading(false)
    }
  }

  const confirmAssign = async () => {
    if (!assignTarget || !selectedBalise) return
    setAssignBusy(true)
    try {
      if (assignTarget.vehicle_source === 'CONTAINER') {
        await baliseService.assignBaliseToContainer(selectedBalise.lockCode, assignTarget.id, assignTarget.vehicle_code)
      } else {
        await baliseService.assignBaliseToAsset(selectedBalise.lockCode, assignTarget.id)
      }
      showMessage?.(t('balise_assigned_to', { defaultValue: 'Balise {{code}} assigned to {{vehicle}}', code: selectedBalise.lockCode, vehicle: assignTarget.vehicle_code }), 'success')
      setAssignTarget(null)
      setSelectedBalise(null)
      await refreshVehicles()
    } catch (error) {
      console.error('Assign error:', error)
      showMessage?.(t('assign_failed', { defaultValue: 'Failed to assign balise' }), 'error')
    } finally {
      setAssignBusy(false)
    }
  }

  // Handle column sorting
  const handleSort = (field: keyof FleetVehicle) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Sort vehicles
  const sortedVehicles = [...vehicles].sort((a, b) => {
    const aValue = a[sortField]
    const bValue = b[sortField]
    
    // Handle different data types
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue)
      return sortDirection === 'asc' ? comparison : -comparison
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      const comparison = aValue - bValue
      return sortDirection === 'asc' ? comparison : -comparison
    } else {
      // Handle mixed types or undefined values
      const aStr = String(aValue || '')
      const bStr = String(bValue || '')
      const comparison = aStr.localeCompare(bStr)
      return sortDirection === 'asc' ? comparison : -comparison
    }
  })

  // Get sort icon for column headers
  const getSortIcon = (field: keyof FleetVehicle) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />
  }

  // Filter vehicles (apply to sorted vehicles)
  const filteredVehicles = sortedVehicles.filter(vehicle => {
    const q = searchTerm.toLowerCase()
    const matchesSearch = vehicle.vehicle_code.toLowerCase().includes(q) ||
                         vehicle.license_plate.toLowerCase().includes(q) ||
                         vehicle.make.toLowerCase().includes(q) ||
                         vehicle.model.toLowerCase().includes(q) ||
                         (vehicle.balise_codes?.toLowerCase().includes(q) || false) ||
                         (vehicle.customer_name?.toLowerCase().includes(q) || false)
    
    const matchesStatus = statusFilter === 'all' || vehicle.tracking_status === statusFilter
    const matchesType = typeFilter === 'all' || vehicle.vehicle_type === typeFilter
    const matchesCustomer = customerFilter === 'all' || vehicle.customer_name === customerFilter
    const matchesFleet =
      fleetFilter === 'all' ? true :
      fleetFilter === '__none__' ? vehicle.fleets.length === 0 :
      vehicle.fleets.includes(fleetFilter)
    const matchesKpi =
      kpiFilter === 'all' ? true :
      kpiFilter === 'LIVE' ? vehicle.tracking_status === 'LIVE' :
      kpiFilter === 'TRACKED' ? vehicle.balise_count > 0 :
      kpiFilter === 'NOT_TRACKED' ? vehicle.balise_count === 0 :
      /* ALERTS */ (vehicle.alert_status === 'NO_SIGNAL' || vehicle.alert_status === 'LOW_BATTERY')

    return matchesSearch && matchesStatus && matchesType && matchesCustomer && matchesFleet && matchesKpi
  })

  // Distinct customers for the per-customer view
  const customerOptions = [...new Set(vehicles.map(v => v.customer_name).filter(Boolean))].sort() as string[]


  const getVehicleTypeIcon = (vehicle: FleetVehicle) => {
    const Icon = vehicleIconComponent(vehicle.icon_key, vehicle.asset_type || vehicle.vehicle_type, vehicle.vehicle_source === 'CONTAINER')
    return <Icon className={`w-5 h-5 ${trackingPresentation(vehicle.tracking_status, vehicle.current_speed).icon}`} />
  }

  // Honest tracking status display — the unified status language:
  // blue = driving, amber = parked, red = no signal, gray = not tracked
  const getTrackingDisplay = (vehicle: FleetVehicle) => {
    switch (vehicle.tracking_status) {
      case 'LIVE': {
        const presentation = trackingPresentation('LIVE', vehicle.current_speed)
        return {
          color: presentation.badge,
          icon: <Activity className="w-4 h-4" />,
          label: t(presentation.labelKey, { defaultValue: presentation.label })
        }
      }
      case 'STALE':
        return { color: 'bg-red-100 text-red-800', icon: <AlertTriangle className="w-4 h-4" />, label: t('tracking_no_signal', { defaultValue: 'No signal' }) }
      case 'OFFLINE':
        return { color: 'bg-red-100 text-red-800', icon: <AlertTriangle className="w-4 h-4" />, label: t('tracking_offline', { defaultValue: 'Offline' }) }
      case 'NOT_TRACKED':
      default:
        return { color: 'bg-gray-100 text-gray-600', icon: <Clock className="w-4 h-4" />, label: t('tracking_not_tracked', { defaultValue: 'Not tracked' }) }
    }
  }

  // Get alert display (tracking-first)
  const getAlertDisplay = (vehicle: FleetVehicle) => {
    switch (vehicle.alert_status) {
      case 'NO_SIGNAL':
        return { color: 'bg-amber-100 text-amber-800', icon: <AlertTriangle className="w-4 h-4" />, label: `${t('tracking_no_signal', { defaultValue: 'No signal' })} ${formatLastSeen(vehicle.balise_last_seen)}` }
      case 'LOW_BATTERY':
        return { color: 'bg-red-100 text-red-800', icon: <AlertTriangle className="w-4 h-4" />, label: `${t('low_battery', { defaultValue: 'Low battery' })} ${vehicle.balise_battery}%` }
      case 'NOT_TRACKED':
        return { color: 'bg-gray-100 text-gray-600', icon: <Clock className="w-4 h-4" />, label: t('tracking_not_tracked', { defaultValue: 'Not tracked' }) }
      case 'OK':
        return { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-4 h-4" />, label: 'OK' }
      default:
        return { color: 'bg-gray-100 text-gray-800', icon: <Clock className="w-4 h-4" />, label: vehicle.alert_status }
    }
  }

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading_vehicles_db')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-3">
              <Truck className="w-8 h-8 text-blue-600" />
              <span>{t('vehicle_management')}</span>
            </h1>
            <p className="text-gray-600 mt-1">
              Real-time vehicle tracking: balise assignments, signal status and positions
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <button 
              onClick={handleNewVehicle}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{t('new_vehicle')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards — clickable: each card filters the table to its vehicles */}
      {vehicleStats && (
        <div className="bg-white border-b border-gray-200 p-6 flex-shrink-0">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {([
              { key: 'all' as const, label: t('total_vehicles'), value: vehicleStats.total_vehicles, bg: 'bg-blue-50', text: 'text-blue-600', num: 'text-blue-900', ring: 'ring-blue-500', icon: <Truck className="w-8 h-8 text-blue-600" /> },
              { key: 'LIVE' as const, label: t('live_now', { defaultValue: 'Live Now' }), value: vehicleStats.live_vehicles, bg: 'bg-green-50', text: 'text-green-600', num: 'text-green-900', ring: 'ring-green-500', icon: <Activity className="w-8 h-8 text-green-600" /> },
              { key: 'TRACKED' as const, label: t('tracked', { defaultValue: 'Tracked' }), value: vehicleStats.tracked_vehicles, bg: 'bg-emerald-50', text: 'text-emerald-600', num: 'text-emerald-900', ring: 'ring-emerald-500', icon: <CheckCircle className="w-8 h-8 text-emerald-600" /> },
              { key: 'NOT_TRACKED' as const, label: t('not_tracked', { defaultValue: 'Not Tracked' }), value: vehicleStats.not_tracked_vehicles, bg: 'bg-gray-100', text: 'text-gray-600', num: 'text-gray-900', ring: 'ring-gray-500', icon: <Clock className="w-8 h-8 text-gray-500" /> },
              { key: 'ALERTS' as const, label: t('alerts'), value: vehicleStats.tracking_alerts, bg: 'bg-red-50', text: 'text-red-600', num: 'text-red-900', ring: 'ring-red-500', icon: <AlertTriangle className="w-8 h-8 text-red-600" /> },
            ]).map(card => {
              const active = kpiFilter === card.key
              return (
                <button
                  key={card.key}
                  onClick={() => setKpiFilter(active ? 'all' : card.key)}
                  className={`${card.bg} rounded-lg p-4 text-left transition-shadow hover:shadow-md ${active ? `ring-2 ${card.ring}` : ''}`}
                  title={t('click_to_filter', { defaultValue: 'Click to show these vehicles in the list' }) as string}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm ${card.text} font-medium`}>{card.label}</p>
                      <p className={`text-2xl font-bold ${card.num}`}>{card.value}</p>
                    </div>
                    {card.icon}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex space-x-8 px-6">
          {[
            { id: 'overview', label: t('overview'), icon: <Activity className="w-4 h-4" /> },
            { id: 'fleets', label: t('fleets', { defaultValue: 'Fleets' }), icon: <Settings className="w-4 h-4" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-auto">
        {activeTab === 'overview' && (
          <div className="h-full flex flex-col p-6">
            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 flex-shrink-0">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                       placeholder="Search by code, plate, make, model or customer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">{t('all_statuses')}</option>
                  <option value="LIVE">{t('tracking_live', { defaultValue: 'Live' })}</option>
                  <option value="STALE">{t('tracking_no_signal', { defaultValue: 'No signal' })}</option>
                  <option value="OFFLINE">{t('tracking_offline', { defaultValue: 'Offline' })}</option>
                  <option value="NOT_TRACKED">{t('tracking_not_tracked', { defaultValue: 'Not tracked' })}</option>
                </select>
                
                <select
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">{t('all_customers', { defaultValue: 'All customers' })}</option>
                  {customerOptions.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                
                <select
                  value={fleetFilter}
                  onChange={(e) => setFleetFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">{t('all_fleets', { defaultValue: 'All fleets' })}</option>
                  <option value="__none__">{t('no_fleet', { defaultValue: 'Not in a fleet' })}</option>
                  {allFleets.map(f => (
                    <option key={f.id} value={f.name}>{f.name} ({f.member_count})</option>
                  ))}
                </select>

                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">{t('all_vehicle_types')}</option>
                  <option value="VEHICLE">Unclassified legacy vehicle</option>
                  <option value="TRACTOR_HEAD">Tractor head</option>
                  <option value="TRUCK">Rigid truck</option>
                  <option value="VAN">Van</option>
                  <option value="PICKUP">Pickup</option>
                  <option value="CAR">Car</option>
                  <option value="TANKER">Fuel tanker / cistern trailer</option>
                  <option value="CONTAINER_CHASSIS">Container chassis</option>
                  <option value="CONTAINER">{t('containers')}</option>
                </select>
              </div>
            </div>

            {/* Vehicle List — fills the remaining tab height so its own
                scrollbars (horizontal included) are always visible without
                scrolling the whole tab first. */}
            <div className="bg-white rounded-lg border border-gray-200 flex-1 min-h-0 flex flex-col">
              <div className="flex-1 min-h-0 overflow-auto">
                <table className="min-w-[1400px] w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      {/* Actions pinned left so they never scroll out of view */}
                      <th className="sticky left-0 z-20 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                        {t('actions')}
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('vehicle_code')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('vehicle')}</span>
                          {getSortIcon('vehicle_code')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('tracking_status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('status')}</span>
                          {getSortIcon('tracking_status')}
                        </div>
                      </th>
                       <th 
                         className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                         onClick={() => handleSort('customer_name')}
                       >
                         <div className="flex items-center space-x-1">
                           <span>{t('customer')}</span>
                           {getSortIcon('customer_name')}
                         </div>
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         {t('fleets', { defaultValue: 'Fleets' })}
                       </th>
                       <th 
                         className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                         onClick={() => handleSort('balise_count')}
                       >
                         <div className="flex items-center space-x-1">
                           <span>{t('balises')}</span>
                           {getSortIcon('balise_count')}
                         </div>
                       </th>
                       <th 
                         className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                         onClick={() => handleSort('balise_battery')}
                       >
                         <div className="flex items-center space-x-1">
                           <span>{t('battery', { defaultValue: 'Battery' })}</span>
                           {getSortIcon('balise_battery')}
                         </div>
                       </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('current_location')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('location')}</span>
                          {getSortIcon('current_location')}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort('alert_status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('alerts')}</span>
                          {getSortIcon('alert_status')}
                        </div>
                      </th>
                    </tr>
                  </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {filteredVehicles.map(vehicle => {
                      const trackingDisplay = getTrackingDisplay(vehicle)
                      const alertDisplay = getAlertDisplay(vehicle)
                      const hasGps = vehicle.last_latitude != null && vehicle.last_longitude != null && (vehicle.last_latitude !== 0 || vehicle.last_longitude !== 0)
                      
                      const rowKey = vehicleKey(vehicle)
                      return (
                        <tr key={rowKey} className="group hover:bg-gray-50">
                          {/* Actions pinned left, mirrors the sticky header cell */}
                          <td className="sticky left-0 z-[5] bg-white group-hover:bg-gray-50 px-4 py-4 whitespace-nowrap text-sm font-medium border-r border-gray-200">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => handleViewVehicle(vehicle)}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                                title={t('view_details')}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => handleEditVehicle(vehicle)}
                                className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-50 transition-colors"
                                title={t('edit_vehicle')}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {onShowOnMap && vehicle.balise_count > 0 && (
                                <button
                                  onClick={() => onShowOnMap(vehicle)}
                                  className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 transition-colors"
                                  title={t('show_on_map', { defaultValue: 'Show on map' })}
                                >
                                  <MapIcon className="w-4 h-4" />
                                </button>
                              )}
                              {vehicle.balise_count > 0 ? (
                                <button 
                                  onClick={() => setUnassignTarget(vehicle)}
                                  className="text-orange-600 hover:text-orange-900 p-1 rounded hover:bg-orange-50 transition-colors"
                                  title={t('unassign_balise', { defaultValue: 'Unassign balise' })}
                                >
                                  <Unlink className="w-4 h-4" />
                                </button>
                              ) : (
                                <button 
                                  onClick={() => openAssignPicker(vehicle)}
                                  className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 transition-colors"
                                  title={t('assign_balise', { defaultValue: 'Assign balise' })}
                                >
                                  <Link2 className="w-4 h-4" />
                                </button>
                              )}
                              {/* Fleet association: explicit per-vehicle action */}
                              <button
                                onClick={() => openFleetDialog(vehicle)}
                                className={`p-1 rounded transition-colors ${
                                  vehicle.fleets.length > 0
                                    ? 'text-purple-600 hover:text-purple-900 hover:bg-purple-50'
                                    : 'text-gray-400 hover:text-purple-700 hover:bg-purple-50'
                                }`}
                                title={vehicle.fleets.length > 0
                                  ? (t('manage_fleet_membership', { defaultValue: 'Fleet: {{fleet}} — change or remove', fleet: vehicle.fleets[0] }) as string)
                                  : (t('associate_to_fleet', { defaultValue: 'Associate to a fleet' }) as string)}
                              >
                                <Boxes className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                  {getVehicleTypeIcon(vehicle)}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {vehicle.custom_name || vehicle.operational_identifier}
                                </div>
                                <div className="text-sm text-gray-500">
                                  <span className="font-medium text-gray-700">{vehicle.operational_identifier}</span>
                                  {vehicle.vehicle_source === 'ASSET'
                                    ? ([vehicle.make, vehicle.model].filter(Boolean).length > 0 ? ` · ${[vehicle.make, vehicle.model].filter(Boolean).join(' ')}` : '')
                                    : (vehicle.container_type ? ` · ${vehicle.container_type}` : '')}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${trackingDisplay.color}`}>
                              {trackingDisplay.icon}
                              <span className="ml-1">{trackingDisplay.label}</span>
                            </div>
                            {vehicle.balise_last_seen && (
                              <div className="text-xs text-gray-400 mt-1">{formatLastSeen(vehicle.balise_last_seen)}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {vehicle.customer_name || 'Not assigned'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {vehicle.customer_code || ''}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {vehicle.fleets.length > 0 ? (
                              <div className="flex flex-wrap gap-1 max-w-[220px]">
                                {vehicle.fleets.map(name => {
                                  const fleetId = allFleets.find(f => f.name === name)?.id
                                  const color = fleetId != null ? fleetColor(fleetId) : null
                                  return (
                                    <span
                                      key={name}
                                      className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                                      style={color ? { backgroundColor: color.bg, color: color.text } : { backgroundColor: '#EEF2FF', color: '#4338CA' }}
                                    >
                                      {name}
                                    </span>
                                  )
                                })}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {vehicle.balise_count > 0 ? `${vehicle.balise_count} ${t('device', { defaultValue: 'device' })}${vehicle.balise_count > 1 ? 's' : ''}` : t('no_balise', { defaultValue: 'No balise' })}
                            </div>
                            <div className="text-sm text-gray-500" title={vehicle.balise_codes || undefined}>
                              {vehicle.balise_codes || t('tracking_not_tracked', { defaultValue: 'Not tracked' })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {vehicle.balise_count > 0 && vehicle.balise_battery != null ? (
                              <div className="flex items-center space-x-2">
                                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${vehicle.balise_battery < 20 ? 'bg-red-500' : vehicle.balise_battery < 50 ? 'bg-amber-500' : 'bg-green-500'}`}
                                    style={{ width: `${Math.min(100, Math.max(0, vehicle.balise_battery))}%` }}
                                  />
                                </div>
                                <span className="text-sm text-gray-700">{vehicle.balise_battery}%</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {hasGps ? (
                              <>
                                <div className="text-sm text-gray-900">
                                  {Number(vehicle.last_latitude).toFixed(4)}, {Number(vehicle.last_longitude).toFixed(4)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {vehicle.balise_last_seen ? formatLastSeen(vehicle.balise_last_seen) : ''}
                                </div>
                              </>
                            ) : (
                              <span className="text-sm text-gray-400">{t('no_position', { defaultValue: 'No position' })}</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${alertDisplay.color}`}>
                              {alertDisplay.icon}
                              <span className="ml-1">{alertDisplay.label}</span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'fleets' && (
          <FleetGroupsPanel
            vehicles={vehicles}
            showMessage={showMessage}
          />
        )}
      </div>

      {/* New Vehicle Modal */}
      {showNewVehicleModal && (
        <VehicleCreationWizard
          isOpen={showNewVehicleModal}
          onClose={() => setShowNewVehicleModal(false)}
          onSuccess={async () => {
            setShowNewVehicleModal(false)
            // Reload vehicles
            const vehicleData = await loadVehicleData()
            setVehicles(vehicleData)
            setVehicleStats(calculateStats(vehicleData))
            showMessage?.('Vehicle created successfully', 'success')
          }}
          showMessage={showMessage}
        />
      )}

      {/* Vehicle Details Modal */}
      {showVehicleDetailsModal && selectedVehicleForDetails && (
        <VehicleDetailsModal
          isOpen={showVehicleDetailsModal}
          vehicle={selectedVehicleForDetails}
          activeTab={vehicleDetailsTab}
          onTabChange={setVehicleDetailsTab}
          startEditing={vehicleModalEditing}
          onClose={() => {
            setShowVehicleDetailsModal(false)
            setSelectedVehicleForDetails(null)
          }}
          onSuccess={async () => {
            // Reload vehicles
            const vehicleData = await loadVehicleData()
            setVehicles(vehicleData)
            setVehicleStats(calculateStats(vehicleData))
            showMessage?.(t('vehicle_updated', { defaultValue: 'Vehicle updated successfully' }), 'success')
          }}
          onShowOnMap={onShowOnMap}
          onCreateZone={onCreateVehicleZone}
          onManageZone={onManageVehicleZone}
          onManageMission={onManageVehicleMission}
          onCreateAlertRule={onCreateVehicleAlertRule}
          onEditAlertRule={onEditVehicleAlertRule}
          showMessage={showMessage}
        />
      )}

      {/* Per-vehicle fleet association dialog: shows the current fleet and
          offers explicit Associate / Move / Dissociate actions */}
      {fleetDialogVehicle && (() => {
        const v = fleetDialogVehicle
        const currentFleetName = v.fleets[0]
        const targetFleet = allFleets.find(f => f.id === Number(fleetDialogFleetId))
        const isMove = !!currentFleetName && !!targetFleet && targetFleet.name !== currentFleetName
        return (
          <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Boxes className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('fleet_association', { defaultValue: 'Fleet association' })}</h3>
                    <p className="text-sm text-gray-500">{v.vehicle_code}</p>
                  </div>
                </div>
                <button onClick={() => setFleetDialogVehicle(null)} className="p-1.5 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Current situation, stated plainly */}
              <div className="mb-4 p-3 rounded-xl bg-gray-50 border border-gray-200 text-sm">
                {currentFleetName ? (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{t('current_fleet', { defaultValue: 'Current fleet' })}</span>
                    <span
                      className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                      style={(() => {
                        const f = allFleets.find(fl => fl.name === currentFleetName)
                        const c = f ? fleetColor(f.id) : null
                        return c ? { backgroundColor: c.bg, color: c.text } : { backgroundColor: '#EEF2FF', color: '#4338CA' }
                      })()}
                    >
                      {currentFleetName}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-500">{t('no_fleet_yet', { defaultValue: 'This vehicle is not in any fleet.' })}</span>
                )}
              </div>

              {/* Associate / move */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  {currentFleetName
                    ? t('move_to_other_fleet', { defaultValue: 'Move to another fleet' })
                    : t('associate_to_fleet', { defaultValue: 'Associate to a fleet' })}
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={fleetDialogFleetId}
                    onChange={e => setFleetDialogFleetId(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">{t('select_fleet', { defaultValue: 'Select a fleet…' })}</option>
                    {allFleets.filter(f => f.name !== currentFleetName).map(f => (
                      <option key={f.id} value={f.id}>{f.name} ({f.member_count})</option>
                    ))}
                  </select>
                  <button
                    disabled={!targetFleet || fleetDialogBusy}
                    onClick={() => targetFleet && assignVehicleToFleet(targetFleet.id, targetFleet.name)}
                    className={`px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-1.5 ${
                      isMove ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {isMove ? <ArrowRightLeft className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                    <span>{fleetDialogBusy ? t('working', { defaultValue: 'Working…' }) : isMove ? t('move', { defaultValue: 'Move' }) : t('associate', { defaultValue: 'Associate' })}</span>
                  </button>
                </div>
                {isMove && (
                  <p className="text-xs text-amber-700">
                    {t('move_warning', { defaultValue: 'The vehicle will leave "{{from}}" — a vehicle belongs to one fleet only.', from: currentFleetName })}
                  </p>
                )}

                {/* Or create a new fleet */}
                <label className="block pt-2 text-xs font-medium text-gray-600 uppercase tracking-wide">
                  {t('or_new_fleet', { defaultValue: 'Or create a new fleet' })}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={fleetDialogNewName}
                    onChange={e => setFleetDialogNewName(e.target.value)}
                    placeholder={t('fleet_name_placeholder', { defaultValue: 'Fleet name…' }) as string}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    disabled={!fleetDialogNewName.trim() || fleetDialogBusy}
                    onClick={createFleetAndAssign}
                    className="px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center space-x-1.5"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{t('create_and_add', { defaultValue: 'Create & add' })}</span>
                  </button>
                </div>
              </div>

              {/* Dissociate */}
              {currentFleetName && (
                <div className="mt-5 pt-4 border-t border-gray-200">
                  <button
                    disabled={fleetDialogBusy}
                    onClick={dissociateVehicleFromFleet}
                    className="w-full px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    <Unlink className="w-4 h-4" />
                    <span>{t('dissociate_from_fleet', { defaultValue: 'Dissociate from fleet "{{fleet}}"', fleet: currentFleetName })}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      })()}

      {/* Unassign balise confirmation */}
      {unassignTarget && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-full max-w-md mx-4">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Unlink className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{t('unassign_balise', { defaultValue: 'Unassign balise' })}</h3>
                <p className="mt-2 text-sm text-gray-600">
                  {t('unassign_balise_confirm', {
                    defaultValue: 'Unassign balise {{code}} from {{vehicle}}? The vehicle will no longer be tracked.',
                    code: (unassignTarget.balise_codes || '').split(',')[0]?.trim(),
                    vehicle: unassignTarget.vehicle_code,
                  })}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setUnassignTarget(null)}
                disabled={unassignBusy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                onClick={confirmUnassign}
                disabled={unassignBusy}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {unassignBusy ? t('processing', { defaultValue: 'Processing…' }) : t('unassign', { defaultValue: 'Unassign' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign balise picker */}
      {assignTarget && (() => {
        const q = assignSearch.trim().toLowerCase()
        const brands = [...new Set(availableBalises.map(b => b.brand).filter(Boolean))].sort()
        const filteredBalises = availableBalises
          .filter(b => assignBrandFilter === 'all' || b.brand === assignBrandFilter)
          .filter(b => !q
            || b.lockCode?.toLowerCase().includes(q)
            || b.universalId?.toLowerCase().includes(q)
            || b.deviceName?.toLowerCase().includes(q)
            || b.brand?.toLowerCase().includes(q))
          // Most recently seen first — those are the devices actually alive
          .sort((a, b) => new Date(b.lastSeen || 0).getTime() - new Date(a.lastSeen || 0).getTime())
        return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg mx-4 flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('assign_balise', { defaultValue: 'Assign balise' })}</h3>
                  <p className="text-sm text-gray-500">{assignTarget.vehicle_code}</p>
                </div>
              </div>
              <button onClick={() => setAssignTarget(null)} className="p-1.5 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Search + brand filter */}
            <div className="px-5 pt-4 pb-2 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  autoFocus
                  value={assignSearch}
                  onChange={(e) => setAssignSearch(e.target.value)}
                  placeholder={t('assign_search_placeholder', { defaultValue: 'Search by code, name or ID…' }) as string}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              {brands.length > 1 && (
                <select
                  value={assignBrandFilter}
                  onChange={(e) => setAssignBrandFilter(e.target.value)}
                  className="px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">{t('all_brands', { defaultValue: 'All brands' })}</option>
                  {brands.map(brand => <option key={brand} value={brand}>{brand}</option>)}
                </select>
              )}
            </div>
            <div className="px-5 pb-1 text-xs text-gray-400">
              {t('assign_results_count', { defaultValue: '{{count}} available', count: filteredBalises.length })}
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-4">
              {assignLoading ? (
                <div className="py-10 text-center text-gray-500">{t('loading')}</div>
              ) : availableBalises.length === 0 ? (
                <div className="py-10 text-center">
                  <Battery className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">{t('no_available_balises', { defaultValue: 'No available balises' })}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('no_available_balises_hint', { defaultValue: 'All balises are already assigned. Unassign one first from Balise Management.' })}</p>
                </div>
              ) : filteredBalises.length === 0 ? (
                <div className="py-10 text-center">
                  <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">{t('no_balise_match', { defaultValue: 'No balise matches your search' })}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredBalises.map(balise => (
                    <button
                      key={balise.lockCode}
                      onClick={() => setSelectedBalise(balise)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-colors ${
                        selectedBalise?.lockCode === balise.lockCode
                          ? 'border-green-500 bg-green-50 ring-1 ring-green-500'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">{balise.lockCode}</div>
                        <div className="text-xs text-gray-500">{balise.brand} · {balise.deviceName || balise.universalId}</div>
                      </div>
                      <div className="flex items-center space-x-3 text-xs text-gray-500">
                        {balise.batteryLevel != null && (
                          <span className={balise.batteryLevel < 20 ? 'text-red-600 font-medium' : ''}>{balise.batteryLevel}%</span>
                        )}
                        {balise.lastSeen && <span>{formatLastSeen(balise.lastSeen)}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setAssignTarget(null)}
                disabled={assignBusy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                onClick={confirmAssign}
                disabled={!selectedBalise || assignBusy}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {assignBusy
                  ? t('processing', { defaultValue: 'Processing…' })
                  : selectedBalise
                    ? t('assign_balise_to', { defaultValue: 'Assign {{code}}', code: selectedBalise.lockCode })
                    : t('select_balise', { defaultValue: 'Select a balise' })}
              </button>
            </div>
          </div>
        </div>
        )
      })()}
    </div>
  )
}

export default FleetVehicleOperations
